
import { useState, useCallback } from "react";
import { AUTH_STORAGE_KEY, SERVER_URL } from "../socket";

function getAuthHeaders() {
  try {
    const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
  } catch { return null; }
}

export default function useCallHistory() {
  const [callHistory, setCallHistory] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);

  /* ── Load all call history from DB ── */
  const loadCallHistory = useCallback(async () => {
    setLoadingCalls(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res  = await fetch(`${SERVER_URL}/api/calls`, { headers });
      const data = await res.json();
      setCallHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load call history", e);
    }
    setLoadingCalls(false);
  }, []);

  /* ── Save a call record to DB ── */
  const saveCall = useCallback(async ({
    caller,
    receiver,
    type      = "voice",     // "voice" | "video"
    status    = "answered",  // "answered" | "missed" | "declined"
    duration  = 0,           // seconds
    startTime,
    endTime,
    groupId   = null,
    groupName = null,
  }) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await fetch(`${SERVER_URL}/api/calls`, {
        method:  "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body:    JSON.stringify({
          caller, receiver, type, status, duration,
          startTime: startTime || Date.now(),
          endTime:   endTime   || Date.now(),
          groupId, groupName,
        }),
      });
      const record = await res.json();
      // Prepend to local list so UI updates instantly
      setCallHistory(prev => [record, ...prev]);
      return record;
    } catch (e) {
      console.error("Failed to save call", e);
    }
  }, []);

  /* ── Delete a single call ── */
  const deleteCall = useCallback(async (id) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await fetch(`${SERVER_URL}/api/calls/${id}`, { method: "DELETE", headers });
      setCallHistory(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      console.error("Failed to delete call", e);
    }
  }, []);

  /* ── Clear all call history ── */
  const clearAllCalls = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await fetch(`${SERVER_URL}/api/calls`, { method: "DELETE", headers });
      setCallHistory([]);
    } catch (e) {
      console.error("Failed to clear calls", e);
    }
  }, []);

  return { callHistory, loadingCalls, loadCallHistory, saveCall, deleteCall, clearAllCalls };
}
